import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { formatDuration } from '../../../utils/durationUtils';
import { formatItemPrice, isRangePriced } from '../../../utils/pricingUtils';

const CatalogImageModal = ({
  isOpen,
  onClose,
  service,
  images,
  initialIndex = 0,
  onAction,
  actionActive,
  isProduct,
}) => {
  const [idx, setIdx] = useState(initialIndex);

  useEffect(() => {
    setIdx(initialIndex);
  }, [initialIndex, isOpen]);

  const prev = useCallback((e) => {
    e?.stopPropagation();
    setIdx(i => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const next = useCallback((e) => {
    e?.stopPropagation();
    setIdx(i => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, prev, next, onClose]);

  if (!isOpen) return null;

  const hasMultiple = images.length > 1;
  const currentUrl = images[idx]?.url;
  const actionLabel = isProduct ? 'Add to Cart' : 'Book Now';
  const activeLabel = isProduct ? 'Added' : 'Booking';

  const stockLabel = isProduct && service?.currentStock != null
    ? service.isOutOfStock ? 'Out of Stock'
      : service.isLowStock ? `${service.currentStock} left`
      : 'In Stock'
    : null;

  const stockColor = service?.isOutOfStock
    ? 'text-destructive'
    : service?.isLowStock
    ? 'text-warning'
    : 'text-success';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Image area */}
        <div className="relative aspect-[4/3] bg-muted">
          {currentUrl ? (
            <img
              src={currentUrl}
              alt={service?.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="ImageOff" size={40} color="var(--color-muted-foreground)" />
            </div>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <Icon name="X" size={15} color="white" />
          </button>

          {/* Popular badge */}
          {service?.isPopular && (
            <div className="absolute top-2.5 left-2.5">
              <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full shadow-md">
                <Icon name="Star" size={11} />
                Popular
              </span>
            </div>
          )}

          {/* Prev / Next */}
          {hasMultiple && (
            <>
              <button
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
              >
                <Icon name="ChevronLeft" size={16} color="white" />
              </button>
              <button
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
              >
                <Icon name="ChevronRight" size={16} color="white" />
              </button>
            </>
          )}

          {/* Dots */}
          {hasMultiple && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`block rounded-full transition-all duration-200 ${
                    i === idx ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="p-4 space-y-3">
          <div>
            <h3 className="font-heading font-bold text-lg text-foreground leading-tight">{service?.name}</h3>
            {service?.category && (
              <p className="text-xs text-muted-foreground font-caption mt-0.5">{service.category}</p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Icon name="ReceiptCent" size={16} color="var(--color-primary)" />
              <span className="font-caption font-bold text-base text-foreground">
                {formatItemPrice(service)}
              </span>
            </div>
            {isRangePriced(service) && (
              <span className="rounded-full bg-warning/10 px-2 py-1 text-xs font-medium text-warning font-caption">
                Final price confirmed later
              </span>
            )}
            {service?.durationInMinutes != null && (
              <div className="flex items-center gap-1.5">
                <Icon name="Clock" size={15} color="var(--color-secondary)" />
                <span className="font-caption text-sm text-muted-foreground">
                  {formatDuration(service.durationInMinutes)}
                </span>
              </div>
            )}
            {stockLabel && (
              <span className={`font-caption text-sm font-semibold ${stockColor}`}>
                {stockLabel}
              </span>
            )}
          </div>

          {service?.description && (
            <p className="text-sm text-foreground/75 font-caption leading-relaxed line-clamp-3">
              {service.description}
            </p>
          )}

          <button
            onClick={onAction}
            disabled={isProduct && service?.isOutOfStock}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm font-caption transition-all duration-200 ${
              actionActive
                ? 'bg-emerald-500 text-white'
                : 'bg-primary text-primary-foreground hover:opacity-90'
            } disabled:opacity-50`}
          >
            <Icon name={actionActive ? 'Check' : isProduct ? 'ShoppingCart' : 'Calendar'} size={16} />
            {actionActive ? activeLabel : actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CatalogImageModal;
